import React from "react";
import './profile.css';
import '../listing/listing.css';
import { isAdmin } from "../../utils/roles";
import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { Capacitor } from "@capacitor/core";

const IconCamera = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z"/><path d="M9 2 7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9Zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5Z"/></svg>
);


const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25ZM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83Z"/></svg>
);

const IconShield = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4Zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8Z"/></svg>
);

const IconUsers = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3Zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3Zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5Zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5Z"/></svg>
);

const IconFlag = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M14.4 6 14 4H5v17h2v-7h5.6l.4 2h7V6h-5.6Z"/></svg>
);

const Profile = ( { isLoggedIn, loginUser, onLogout } ) => {
  const { profileId } = useParams();
  const [profileUser, setProfileUser] = useState({});
  const [profileFormData, setProfileFormData] = useState({userName: loginUser.userName || '', phoneNumber: loginUser.phoneNumber || '', gender: loginUser.gender || '', bio: loginUser.bio || ''});
  const [error, setError] = useState("");
  const [isProfileEditMode, setIsProfileEditMode] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(null);
  const [stats, setStats] = useState({ ridesPublished: 0, ridesCompleted: 0, ridesAsPassenger: 0, passengersServed: 0, ridesCancelled: 0, lateUnbooks: 0, disputeCount: 0, noShowCount: 0 });
  const [loading, setLoading] = useState(true);
  const [ridesTab, setRidesTab] = useState("offered"); // offered | booked
  const [offeredRides, setOfferedRides] = useState([]);
  const [bookedRides, setBookedRides] = useState([]);
  // Emergency contacts are private — seeded from the logged-in user (not the
  // public profile endpoint, which never returns them).
  const [emergencyContacts, setEmergencyContacts] = useState(
    Array.isArray(loginUser.emergencyContacts) ? loginUser.emergencyContacts.map((c) => ({ name: c.name, phone: c.phone })) : []
  );
  const [cnicNumber, setCnicNumber] = useState("");
  const [cnicFile, setCnicFile] = useState(null);
  const [verifSubmitting, setVerifSubmitting] = useState(false);
  const navigate = useNavigate();

  const isOwnProfile = !profileId || profileId === loginUser._id;

  useEffect(() => {
    const getProfile = async () => {
      setLoading(true);
      try {
        const userId = profileId || loginUser._id;
        if (userId) {
          const response = await axios.get(`/api/v1/user/${userId}`);
          setProfileUser(response.data.user);
          if (!profileId || profileId === loginUser._id) {
            const u = response.data.user;
            setProfileFormData({ userName: u.userName || '', phoneNumber: u.phoneNumber || '', gender: u.gender || '', bio: u.bio || '' });
          }
        }
      } catch (error) {
          if (error.response && error.response.status === 401) {
            onLogout();
            navigate('/login');
          }
      } finally {
        setLoading(false);
      }
  };

    getProfile();
  }, [profileId, loginUser, onLogout, navigate]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const userId = profileId || loginUser._id;
        if (!userId) return;
        const response = await axios.get(`/api/v1/review/${userId}`);
        setReviews(response.data.reviews || []);
        setAvgRating(response.data.avgRating);
      } catch (error) {
        // Reviews are optional, fail silently
      }
    };
    fetchReviews();
  }, [profileId, loginUser._id]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const userId = profileId || loginUser._id;
        if (!userId) return;
        const response = await axios.get(`/api/v1/user/${userId}/stats`);
        if (response.data) {
          setStats({
            ridesPublished: response.data.ridesPublished ?? 0,
            ridesCompleted: response.data.ridesCompleted ?? 0,
            ridesAsPassenger: response.data.ridesAsPassenger ?? 0,
            passengersServed: response.data.passengersServed ?? 0,
            ridesCancelled: response.data.ridesCancelled ?? 0,
            lateUnbooks: response.data.lateUnbooks ?? 0,
            disputeCount: response.data.disputeCount ?? 0,
            noShowCount: response.data.noShowCount ?? 0,
          });
        }
      } catch (error) {
        // Stats are optional, fail silently
      }
    };
    fetchStats();
  }, [profileId, loginUser._id]);

  useEffect(() => {
    const fetchRides = async () => {
      try {
        const userId = profileId || loginUser._id;
        if (!userId) return;
        const own = !profileId || profileId === loginUser._id;
        const offeredReq = own
          ? axios.get(`/api/v1/ride/own`)
          : axios.get(`/api/v1/ride/${userId}`);
        const bookedReq = axios.get(`/api/v1/ride/bookedRides/${userId}`);
        const [offRes, bookRes] = await Promise.all([offeredReq, bookedReq]);
        const sortFn = (a, b) => {
          if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
          return new Date(b.estimatedDate) - new Date(a.estimatedDate);
        };
        let offered = (offRes.data.rides || []).slice().sort(sortFn);
        let booked = (bookRes.data.bookedRides || []).slice().sort(sortFn);
        // For other users, show only active (not completed, not deleted)
        if (!own) {
          offered = offered.filter((r) => !r.isCompleted && !r.isDeleted);
          booked = booked.filter((r) => !r.isCompleted && !r.isDeleted);
        }
        setOfferedRides(offered);
        setBookedRides(booked);
      } catch {
        // optional
      }
    };
    fetchRides();
  }, [profileId, loginUser._id]);

  const formatRideDate = (estimatedDate) => {
    if (!estimatedDate) return '';
    const date = new Date(estimatedDate);
    if (isNaN(date.getTime())) return '';
    const oneDayInMs = 24 * 60 * 60 * 1000;
    if (Math.abs(date - Date.now()) < oneDayInMs) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const response = await axios.put(`/api/v1/user/avatar/${loginUser._id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfileUser((prev) => ({ ...prev, avatar: response.data.avatar }));
      toast.success("Avatar updated!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update avatar.");
    }
  };

  const handleNativeAvatarUpload = async () => {
    try {
      const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Base64,
        source: CameraSource.Prompt,
        promptLabelHeader: "Profile Photo",
        promptLabelPhoto: "Choose from Gallery",
        promptLabelPicture: "Take a Photo",
        width: 512,
        height: 512,
      });

      const byteString = atob(image.base64String);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: `image/${image.format || "jpeg"}` });
      const file = new File([blob], `avatar.${image.format || "jpg"}`, { type: blob.type });
      const formData = new FormData();
      formData.append("avatar", file);

      const response = await axios.put(`/api/v1/user/avatar/${loginUser._id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfileUser((prev) => ({ ...prev, avatar: response.data.avatar }));
      toast.success("Avatar updated!");
    } catch (err) {
      if (err.message !== "User cancelled photos app") {
        toast.error(err.response?.data?.message || "Failed to update avatar.");
      }
    }
  };

  const handleUpdateProfile = async (event) => {
    event.preventDefault();
    setError('');

    const payload = {
      userName: profileFormData.userName,
      phoneNumber: profileFormData.phoneNumber,
      gender: profileFormData.gender,
      bio: profileFormData.bio,
    };

    // Drop fully-empty rows; a half-filled row is a user error.
    const cleanedContacts = emergencyContacts
      .map((c) => ({ name: (c.name || "").trim(), phone: (c.phone || "").trim() }))
      .filter((c) => c.name || c.phone);
    if (cleanedContacts.some((c) => !c.name || !c.phone)) {
      setError("Each emergency contact needs both a name and a phone number.");
      return;
    }

    try {
        // Save emergency contacts first (separate, private endpoint) so any
        // validation error surfaces before we reload the page.
        const ecRes = await axios.post(`/api/v1/user/${profileUser._id}/emergency-contacts?_method=PUT`, { emergencyContacts: cleanedContacts });
        // Persist to the cached login user so it survives the reload below.
        try {
          const lu = JSON.parse(localStorage.getItem("loginUser")) || {};
          lu.emergencyContacts = ecRes.data.emergencyContacts;
          localStorage.setItem("loginUser", JSON.stringify(lu));
        } catch (e) { /* non-critical */ }

        await axios.post(`/api/v1/user/${profileUser._id}?_method=PUT`, payload);
        setIsProfileEditMode(false);
        window.location.reload();
    } catch (error) {
        if (error.response) {
          setError(error.response.data.message);
        } else {
          setError('An error occurred on Profile update. Please try again');
        }
    }
  };

  const handleContactChange = (index, field, value) => {
    setError('');
    setEmergencyContacts((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const handleAddContact = () => {
    setEmergencyContacts((prev) => (prev.length >= 3 ? prev : [...prev, { name: "", phone: "" }]));
  };

  const handleRemoveContact = (index) => {
    setEmergencyContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProfileChange = (event) => {
    setError('');
    const { name, value } = event.target;
    setProfileFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const handleSubmitVerification = async (event) => {
    event.preventDefault();
    if (!cnicFile) {
      toast.error("Please upload a photo of your CNIC.");
      return;
    }
    const formData = new FormData();
    formData.append("cnicNumber", cnicNumber);
    formData.append("cnic", cnicFile);
    setVerifSubmitting(true);
    try {
      const res = await axios.post("/api/v1/user/verify-identity", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfileUser((prev) => ({ ...prev, verificationStatus: res.data.verificationStatus }));
      try {
        const lu = JSON.parse(localStorage.getItem("loginUser")) || {};
        lu.verificationStatus = res.data.verificationStatus;
        localStorage.setItem("loginUser", JSON.stringify(lu));
      } catch (e) { /* non-critical */ }
      setCnicNumber("");
      setCnicFile(null);
      toast.success(res.data.message || "Submitted for review.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit verification.");
    } finally {
      setVerifSubmitting(false);
    }
  };

  // --- WhatsApp ownership verification (reverse OTP) ---
  // We never message the user. They send us a code from their own WhatsApp; the
  // sender's number is the proof. So the UI's job is to hand over a code and a
  // deep link, then wait for the server to confirm the message arrived.
  const [waChallenge, setWaChallenge] = useState(null); // { code, waLink, listenerNumber }
  const [waStarting, setWaStarting] = useState(false);

  const handleStartWhatsappVerify = async () => {
    setWaStarting(true);
    try {
      const res = await axios.post("/api/v1/user/whatsapp/start");
      setWaChallenge(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't start WhatsApp verification.");
    } finally {
      setWaStarting(false);
    }
  };

  // Poll while a challenge is open. The socket push is the primary signal, but a
  // user who switched to WhatsApp may come back to a dropped socket, and a badge
  // that silently fails to appear reads as "it didn't work".
  useEffect(() => {
    if (!waChallenge || profileUser.whatsappVerified) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get("/api/v1/user/whatsapp/status");
        if (res.data.status === "verified") {
          setWaChallenge(null);
          setProfileUser((prev) => ({ ...prev, whatsappVerified: true }));
          try {
            const lu = JSON.parse(localStorage.getItem("loginUser")) || {};
            lu.whatsappVerified = true;
            localStorage.setItem("loginUser", JSON.stringify(lu));
          } catch (e) { /* non-critical */ }
          toast.success("WhatsApp verified ✅");
        }
      } catch (e) { /* transient — keep polling until the challenge expires */ }
    }, 3000);
    // Challenges live 10 minutes; stop polling when the code can no longer work.
    const stop = setTimeout(() => { clearInterval(id); setWaChallenge(null); }, 10 * 60 * 1000);
    return () => { clearInterval(id); clearTimeout(stop); };
  }, [waChallenge, profileUser.whatsappVerified]);

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("spam");
  const [reportDesc, setReportDesc] = useState("");

  const handleReport = async () => {
    try {
      await axios.post("/api/v1/report/create", {
        reportedUser: profileUser._id,
        reason: reportReason,
        description: reportDesc,
      });
      toast.success("Report submitted");
      setShowReportModal(false);
      setReportReason("spam");
      setReportDesc("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit report");
    }
  };

  if (!isLoggedIn) {
    navigate('/');
    return;
  }

  if (loading) {
    return (
      <div className="sr-profile">
        <div className="sr-profile__top">
          <div className="sr-profile__top-content">
            <div className="sr-profile__hero">
              <div className="sr-profile__avatar-ring"><div className="sr-profile__skeleton sr-profile__skeleton--avatar" /></div>
              <div className="sr-profile__skeleton sr-profile__skeleton--name" />
              <div className="sr-profile__skeleton sr-profile__skeleton--badge" />
            </div>
          </div>
        </div>
        <div className="sr-profile__body">
          <div className="sr-profile__skeleton sr-profile__skeleton--section-title" />
          <div className="sr-profile__fields">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="sr-profile__field">
                <div className="sr-profile__skeleton sr-profile__skeleton--icon" />
                <div className="sr-profile__field-body">
                  <div className="sr-profile__skeleton sr-profile__skeleton--label" />
                  <div className="sr-profile__skeleton sr-profile__skeleton--value" />
                </div>
              </div>
            ))}
          </div>
          <div className="sr-profile__stats">
            {[1, 2, 3].map((i) => (
              <div key={i} className="sr-profile__stat">
                <div className="sr-profile__skeleton sr-profile__skeleton--stat-num" />
                <div className="sr-profile__skeleton sr-profile__skeleton--stat-label" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= rating ? "sr-profile__rating-star--filled" : "sr-profile__rating-star--empty"}>
          ★
        </span>
      );
    }
    return stars;
  };

  if (isProfileEditMode) {
    return(
      <div className="sr-profile">
        <div className="sr-profile__top">
          <div className="sr-profile__top-content">
            <h1 className="sr-profile__edit-title">Edit Profile</h1>
          </div>
        </div>
        <div className="sr-profile__body">
        <div className="sr-profile__edit">

          <form className="sr-profile__edit-form" onSubmit={handleUpdateProfile}>
            <div className="sr-profile__edit-group">
              <label className="sr-profile__edit-label" htmlFor="userName">Name</label>
              <input
                className="sr-profile__edit-input"
                type="text"
                id="userName"
                name="userName"
                value={profileFormData.userName}
                onChange={handleProfileChange}
                placeholder="Your name"
              />
            </div>

            <div className="sr-profile__edit-group">
              <label className="sr-profile__edit-label" htmlFor="bio">Bio</label>
              <textarea
                className="sr-profile__edit-input sr-profile__edit-textarea"
                id="bio"
                name="bio"
                value={profileFormData.bio}
                onChange={handleProfileChange}
                placeholder="e.g. Daily Lahore ↔ Islamabad"
                maxLength={28}
                rows={2}
              />
              <span className="sr-profile__edit-charcount">{profileFormData.bio.length}/28</span>
            </div>

            <div className="sr-profile__edit-row">
              <div className="sr-profile__edit-group">
                <label className="sr-profile__edit-label" htmlFor="phoneNumber">Phone Number</label>
                <input
                  className="sr-profile__edit-input"
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={profileFormData.phoneNumber}
                  onChange={handleProfileChange}
                  placeholder={profileUser.phoneNumber || "+92 300 1234567"}
                />
              </div>

              <div className="sr-profile__edit-group">
                <label className="sr-profile__edit-label" htmlFor="gender">Gender</label>
                <select
                  className="sr-profile__edit-select"
                  id="gender"
                  name="gender"
                  value={profileFormData.gender}
                  onChange={handleProfileChange}
                >
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>

            <div className="sr-profile__edit-group">
              <label className="sr-profile__edit-label">Emergency Contacts <span className="sr-profile__edit-hint">(alerted when you trigger an SOS)</span></label>
              {emergencyContacts.map((contact, index) => (
                <div className="sr-profile__contact-row" key={index}>
                  <input
                    className="sr-profile__edit-input"
                    type="text"
                    value={contact.name}
                    onChange={(e) => handleContactChange(index, "name", e.target.value)}
                    placeholder="Name"
                    maxLength={60}
                  />
                  <input
                    className="sr-profile__edit-input"
                    type="tel"
                    value={contact.phone}
                    onChange={(e) => handleContactChange(index, "phone", e.target.value)}
                    placeholder="e.g. +923001234567"
                    maxLength={20}
                  />
                  <button type="button" className="sr-profile__contact-remove" onClick={() => handleRemoveContact(index)} aria-label="Remove contact">&times;</button>
                </div>
              ))}
              {emergencyContacts.length < 3 && (
                <button type="button" className="sr-profile__contact-add" onClick={handleAddContact}>
                  + Add contact
                </button>
              )}
            </div>

            {error && <p className="sr-profile__edit-error">{error}</p>}

            <div className="sr-profile__edit-actions">
              <button className="sr-profile__btn sr-profile__btn--secondary" type="button" onClick={() => setIsProfileEditMode(false)}>
                Cancel
              </button>
              <button className="sr-profile__btn sr-profile__btn--primary" type="submit">
                Save Changes
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sr-profile">
      {/* ── Dark top section ── */}
      <div className="sr-profile__top">
        <div className="sr-profile__top-content">
          <div className="sr-profile__hero">
          <div className="sr-profile__avatar-ring">
            <img src={profileUser.avatar} alt={profileUser.userName || "User avatar"} />
            {isOwnProfile && (
              Capacitor.isNativePlatform() ? (
                <button className="sr-profile__avatar-upload" onClick={handleNativeAvatarUpload} title="Change photo" type="button">
                  <IconCamera />
                </button>
              ) : (
                <label className="sr-profile__avatar-upload" htmlFor="avatar-upload" title="Change photo">
                  <IconCamera />
                  <input id="avatar-upload" type="file" accept=".jpg,.jpeg,.png" onChange={handleAvatarUpload} />
                </label>
              )
            )}
          </div>

          <h1 className="sr-profile__name">{profileUser.userName}</h1>

          {profileUser.bio && (
            <p className="sr-profile__bio">{profileUser.bio}</p>
          )}

          <div className="sr-profile__badges">
            {isAdmin(profileUser) && (
              <span className="sr-profile__badge sr-profile__badge--admin">
                <IconShield /> Admin
              </span>
            )}
            {profileUser.isApproved && !isAdmin(profileUser) && (
              <span className="sr-profile__badge sr-profile__badge--trusted">
                <IconShield /> Admin Verified
              </span>
            )}
            {profileUser.verificationStatus === "verified" && (
              <span className="sr-profile__badge sr-profile__badge--verified">
                <IconShield /> ID Verified
              </span>
            )}
            {profileUser.whatsappVerified && (
              <span className="sr-profile__badge sr-profile__badge--whatsapp">
                <IconShield /> WhatsApp Verified
              </span>
            )}
          </div>

          {isOwnProfile && (
            <button className="sr-profile__hero-edit-btn" onClick={() => setIsProfileEditMode(true)} type="button">
              <IconEdit /> Edit Profile
            </button>
          )}

          {avgRating && (
            <div className="sr-profile__rating">
              <span className="sr-profile__rating-heading">Reviews</span>
              <div className="sr-profile__rating-stars">{renderStars(Math.round(avgRating))}</div>
              <span className="sr-profile__rating-text">{avgRating.toFixed(1)} ({reviews.length})</span>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* ── White body section ── */}
      <div className="sr-profile__body">

        {/* ── WhatsApp ownership verification (own profile only) ── */}
        {isOwnProfile && !profileUser.whatsappVerified && (
          <div className="sr-profile__verify">
            {!waChallenge ? (
              <div className="sr-profile__verify-form">
                <div className="sr-profile__verify-head">
                  <IconShield />
                  <div>
                    <h3 className="sr-profile__verify-title">Verify your WhatsApp</h3>
                    <p className="sr-profile__verify-sub">
                      Get a WhatsApp Verified badge, and automatically claim rides you post in WhatsApp groups.
                    </p>
                  </div>
                </div>
                <button
                  className="sr-profile__verify-btn"
                  type="button"
                  onClick={handleStartWhatsappVerify}
                  disabled={waStarting}
                >
                  {waStarting ? "Starting…" : "Verify WhatsApp"}
                </button>
              </div>
            ) : (
              <div className="sr-profile__verify-form">
                <div className="sr-profile__verify-head">
                  <IconShield />
                  <div>
                    <h3 className="sr-profile__verify-title">Send us this code</h3>
                    <p className="sr-profile__verify-sub">
                      Tap the button to open WhatsApp and send the message. We'll verify it's you from
                      the number it comes from — we never message you first.
                    </p>
                  </div>
                </div>
                <div className="sr-profile__verify-code">{waChallenge.code}</div>
                <a
                  className="sr-profile__verify-btn"
                  href={waChallenge.waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open WhatsApp
                </a>
                {/* Fallback for desktop browsers that can't hand off to wa.me. */}
                <p className="sr-profile__verify-sub">
                  Or send <strong>{waChallenge.code}</strong> to <strong>+{waChallenge.listenerNumber}</strong> from
                  the WhatsApp number you want verified. This code expires in 10 minutes.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Identity verification (own profile only) ── */}
        {isOwnProfile && profileUser.verificationStatus !== "verified" && (
          <div className="sr-profile__verify">
            {profileUser.verificationStatus === "pending" ? (
              <div className="sr-profile__verify-status sr-profile__verify-status--pending">
                ⏳ Your CNIC is pending review. We'll notify you once it's verified.
              </div>
            ) : (
              <form className="sr-profile__verify-form" onSubmit={handleSubmitVerification}>
                <div className="sr-profile__verify-head">
                  <IconShield />
                  <div>
                    <h3 className="sr-profile__verify-title">Verify your identity</h3>
                    <p className="sr-profile__verify-sub">Get a Verified badge so riders trust you. Your CNIC is private and only seen by our team.</p>
                  </div>
                </div>
                {profileUser.verificationStatus === "rejected" && (
                  <p className="sr-profile__verify-rejected">
                    Your last submission was rejected{profileUser.verificationRejectionReason ? `: ${profileUser.verificationRejectionReason}` : ""}. Please try again.
                  </p>
                )}
                <input
                  className="sr-profile__edit-input"
                  type="text"
                  inputMode="numeric"
                  value={cnicNumber}
                  onChange={(e) => setCnicNumber(e.target.value)}
                  placeholder="CNIC number (e.g. 35202-1234567-1)"
                  maxLength={18}
                />
                <label className="sr-profile__verify-file">
                  {cnicFile ? cnicFile.name : "Upload a photo of your CNIC"}
                  <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => setCnicFile(e.target.files[0] || null)} />
                </label>
                <button className="sr-profile__btn sr-profile__btn--primary" type="submit" disabled={verifSubmitting}>
                  {verifSubmitting ? "Submitting…" : "Submit for verification"}
                </button>
              </form>
            )}
          </div>
        )}

        {/* ── Stats ── */}
        <div className="sr-profile__stats">
          <div className="sr-profile__stat" title="Rides this user has driven and completed">
            <span className="sr-profile__stat-value">{stats.ridesCompleted}</span>
            <span className="sr-profile__stat-label">Offered</span>
          </div>
          <div className="sr-profile__stat" title="Rides booked as a passenger">
            <span className="sr-profile__stat-value">{stats.ridesAsPassenger}</span>
            <span className="sr-profile__stat-label">Taken</span>
          </div>
          {(() => {
            const rate = stats.ridesPublished > 0 ? Math.round((stats.ridesCancelled / stats.ridesPublished) * 100) : 0;
            const modifier = rate === 0 ? 'good' : rate <= 10 ? 'good' : rate >= 50 ? 'danger' : 'warning';
            return (
              <div className={`sr-profile__stat sr-profile__stat--${modifier}`} title="Percentage of published rides that were cancelled">
                <span className="sr-profile__stat-value">{rate}%</span>
                <span className="sr-profile__stat-label">Cancelled</span>
              </div>
            );
          })()}
          <div className="sr-profile__stat" title="Total passengers served across completed rides">
            <span className="sr-profile__stat-value">{stats.passengersServed}</span>
            <span className="sr-profile__stat-label">Passengers</span>
          </div>
          {(() => {
            const disputes = stats.disputeCount;
            const modifier = disputes === 0 ? 'good' : disputes <= 2 ? 'warning' : 'danger';
            return (
              <div className={`sr-profile__stat sr-profile__stat--${modifier}`} title="Reports filed by other users that resulted in dispute">
                <span className="sr-profile__stat-value">{disputes}</span>
                <span className="sr-profile__stat-label">Disputes</span>
              </div>
            );
          })()}
          {(() => {
            const noShows = stats.noShowCount;
            const modifier = noShows === 0 ? 'good' : noShows <= 2 ? 'warning' : 'danger';
            return (
              <div className={`sr-profile__stat sr-profile__stat--${modifier}`} title="Times this user booked a ride but did not show up">
                <span className="sr-profile__stat-value">{noShows}</span>
                <span className="sr-profile__stat-label">No-shows</span>
              </div>
            );
          })()}
          {(() => {
            const lateUnbooks = stats.lateUnbooks;
            const modifier = lateUnbooks === 0 ? 'good' : lateUnbooks <= 2 ? 'warning' : 'danger';
            return (
              <div className={`sr-profile__stat sr-profile__stat--${modifier}`} title="Bookings cancelled less than 24 hours before departure">
                <span className="sr-profile__stat-value">{lateUnbooks}</span>
                <span className="sr-profile__stat-label">Late unbooks</span>
              </div>
            );
          })()}
        </div>

        {/* ── Profile Fields ── */}
        <div className="sr-profile__fields-header">
          <span className="sr-profile__fields-title">User Info</span>
        </div>
        <div className="sr-profile__fields">
          <div className="sr-profile__field-row">
            {(isOwnProfile || profileUser.phoneNumber) && (
            <div className="sr-profile__field sr-profile__field--wide">
              <div className="sr-profile__field-body">
                <div className="sr-profile__field-label">Phone</div>
                <div className={`sr-profile__field-value ${!profileUser.phoneNumber ? 'sr-profile__field-value--nil' : ''}`}>
                  {profileUser.phoneNumber || "Not provided"}
                  {profileUser.phoneNumber && (
                    <button className="sr-profile__copy-btn" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(profileUser.phoneNumber); toast.success("Phone copied"); }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1Zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2Zm0 16H8V7h11v14Z"/></svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
            )}

            <div className="sr-profile__field">
              <div className="sr-profile__field-body">
                <div className="sr-profile__field-label">Joined</div>
                <div className="sr-profile__field-value">
                  {profileUser.createdAt ? (() => {
                    const diff = Date.now() - new Date(profileUser.createdAt).getTime();
                    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                    const weeks = Math.floor(days / 7);
                    const months = Math.floor(days / 30);
                    if (days < 7) return 'This week';
                    if (weeks === 1) return '1 week';
                    if (months < 1) return `${weeks} weeks`;
                    if (months === 1) return '1 month';
                    if (months < 12) return `${months} months`;
                    return new Date(profileUser.createdAt).getFullYear().toString();
                  })() : ''}
                </div>
              </div>
            </div>
          </div>

          <div className="sr-profile__field-row">
            {isOwnProfile && (
              <div className="sr-profile__field sr-profile__field--wide">
                <div className="sr-profile__field-body">
                  <div className="sr-profile__field-label">Email</div>
                  <div className="sr-profile__field-value sr-profile__field-value--lowercase">{profileUser.email}</div>
                </div>
              </div>
            )}

            <div className="sr-profile__field">
              <div className="sr-profile__field-body">
                <div className="sr-profile__field-label">Gender</div>
                <div className="sr-profile__field-value">{
                  { 0: "Female", 1: "Male", 2: "Custom", "female": "Female", "male": "Male", "custom": "Custom" }[profileUser.gender] || profileUser.gender || "Not provided"
                }</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Actions ── */}
        {!isOwnProfile && isAdmin(loginUser) && (
          <div className="sr-profile__actions">
            <Link to={`/listing/${profileUser._id}`} style={{ textDecoration: 'none', width: '100%' }}>
              <button className="sr-profile__btn sr-profile__btn--secondary" style={{ width: '100%' }}>
                <IconUsers /> View Listings
              </button>
            </Link>
          </div>
        )}

        {/* ── Rides (merged from Listing) ── */}
        <div className="sr-profile__rides-section">
          <div className="sr-profile__fields-header">
            <span className="sr-profile__fields-title">{isOwnProfile ? "My Rides" : "Active Rides"}</span>
          </div>
          {isOwnProfile && (
            <div className="sr-listing__toggle" style={{ marginBottom: 12 }}>
              <input type="radio" name="profile-rides" id="profile-offered" checked={ridesTab === "offered"} onChange={() => setRidesTab("offered")} />
              <input type="radio" name="profile-rides" id="profile-booked" checked={ridesTab === "booked"} onChange={() => setRidesTab("booked")} />
              <label htmlFor="profile-offered" className={`sr-listing__toggle-label ${ridesTab === "offered" ? 'sr-listing__toggle-label--active' : ''}`} onClick={() => setRidesTab("offered")}>Offered</label>
              <label htmlFor="profile-booked" className={`sr-listing__toggle-label ${ridesTab === "booked" ? 'sr-listing__toggle-label--active' : ''}`} onClick={() => setRidesTab("booked")}>Booked</label>
              <div className={`sr-listing__toggle-slider ${ridesTab === "booked" ? 'sr-listing__toggle-slider--right' : ''}`} />
            </div>
          )}
          <div className="sr-listing__rides">
            {(() => {
              const list = isOwnProfile ? (ridesTab === "offered" ? offeredRides : bookedRides) : [...offeredRides, ...bookedRides];
              if (list.length === 0) {
                return <div className="sr-listing__empty">{isOwnProfile ? (ridesTab === "offered" ? "No rides posted yet." : "No rides booked yet.") : "No active rides."}</div>;
              }
              return list.map((ride) => (
                <div
                  onClick={() => navigate(`/ride/${ride._id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/ride/${ride._id}`); } }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Ride from ${ride.departure} to ${ride.arrival}`}
                  className={`sr-listing__card${ride.isCompleted ? " sr-listing__card--completed" : ""}`}
                  key={ride._id}
                >
                  <div className="sr-listing__card-dots">
                    <span className="sr-listing__card-dot sr-listing__card-dot--from" />
                    <span className="sr-listing__card-connector" />
                    <span className="sr-listing__card-dot sr-listing__card-dot--to" />
                  </div>
                  <div className="sr-listing__card-route">
                    <div className="sr-listing__card-cities">
                      <span className="sr-listing__card-city">{ride.departure}</span>
                      {ride.route && ride.route !== "nil" && (
                        <span className="sr-listing__card-via">
                          {ride.route.length > 18 ? `${ride.route.substring(0, 15)}...` : ride.route}
                        </span>
                      )}
                      <span className="sr-listing__card-city">{ride.arrival}</span>
                    </div>
                  </div>
                  <span className="sr-listing__card-date">{formatRideDate(ride.estimatedDate)}</span>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* ── Reviews ── */}
        <div className="sr-profile__reviews">
          <h3 className="sr-profile__reviews-header">Reviews</h3>
          {reviews.length > 0 ? (
            <div className="sr-profile__review-list">
              {reviews.map((review) => {
                const rideUserId = review.ride?.user?._id || review.ride?.user;
                const isAsDriver = rideUserId ? rideUserId.toString() === (profileId || loginUser._id) : false;
                return (
                  <div key={review._id} className="sr-profile__review">
                    <div className="sr-profile__review-top">
                      <div className="sr-profile__review-author-row">
                        <Link to={`/profile/${review.reviewer._id}`} className="sr-profile__review-author">
                          {review.reviewer.userName}
                        </Link>
                        <span className={`sr-profile__review-badge ${isAsDriver ? 'sr-profile__review-badge--driver' : 'sr-profile__review-badge--passenger'}`}>
                          {isAsDriver ? "as driver" : "as passenger"}
                        </span>
                      </div>
                      <div className="sr-profile__review-stars">{renderStars(review.rating)}</div>
                    </div>
                    {review.comment && <p className="sr-profile__review-comment">{review.comment}</p>}
                    <p className="sr-profile__review-date">{new Date(review.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="sr-profile__reviews-empty">
              <div className="sr-profile__reviews-empty-stars">★ ★ ★ ★ ★</div>
              <p className="sr-profile__reviews-empty-text">No reviews yet</p>
              <p className="sr-profile__reviews-empty-sub">Reviews will appear here after completed rides</p>
            </div>
          )}
        </div>

        {/* ── Report User ── */}
        {!isOwnProfile && (
          <button className="sr-profile__report-btn" onClick={() => setShowReportModal(true)}>
            <IconFlag /> Report User
          </button>
        )}

        {/* ── Report Modal ── */}
        {showReportModal && (
          <>
            <div className="sr-profile__modal-overlay" onClick={() => setShowReportModal(false)} />
            <div className="sr-profile__modal">
              <h3 className="sr-profile__modal-title">Report {profileUser.userName}</h3>
              <div className="sr-profile__edit-group">
                <label className="sr-profile__edit-label" htmlFor="report-reason">Reason</label>
                <select
                  className="sr-profile__edit-select"
                  id="report-reason"
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                >
                  <option value="spam">Spam</option>
                  <option value="harassment">Harassment</option>
                  <option value="inappropriate">Inappropriate</option>
                  <option value="fraud">Fraud</option>
                  <option value="safety">Safety Concern</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="sr-profile__edit-group">
                <label className="sr-profile__edit-label" htmlFor="report-desc">Details (optional)</label>
                <input
                  className="sr-profile__edit-input"
                  type="text"
                  id="report-desc"
                  value={reportDesc}
                  onChange={(e) => setReportDesc(e.target.value)}
                  placeholder="Tell us more..."
                  maxLength={200}
                />
              </div>
              <div className="sr-profile__edit-actions">
                <button className="sr-profile__btn sr-profile__btn--secondary" type="button" onClick={() => setShowReportModal(false)}>
                  Cancel
                </button>
                <button className="sr-profile__btn sr-profile__btn--danger" type="button" onClick={handleReport}>
                  Submit Report
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
